import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * reconciliation — deterministic cache-merge semantics for the race between
 * REST responses (queries/mutations) and live SignalR pushes (A5-025).
 *
 * ## The problem
 *
 * Three writers touch the same cache entry with no defined ordering:
 *
 *  1. A query/mutation HTTP response (can be slow — 60–180 s on CPU LLM paths).
 *  2. A SignalR live push (`...Updated` / `...Changed` events).
 *  3. A subsequent invalidation-triggered refetch.
 *
 * Before this, `useSignalREvent` set local state then *invalidated* the query,
 * which schedules a REST refetch. If a mutation's slow response (or that
 * refetch) landed AFTER a fresher live push, it clobbered the newer pushed
 * value — the UI silently rolled back to stale data. The only guard
 * (`if (prev) return prev`) covered first-hydration, nothing else.
 *
 * ## The rule (last-writer-by-logical-time, push-biased)
 *
 * Every value carries a logical timestamp (`dataUpdatedAt`, epoch ms). A merge
 * applies the *incoming* value only if it is at least as fresh as the value
 * already in the cache. Ties go to the live push (push wins within the
 * staleness window) so an out-of-order slow REST response can never overwrite a
 * concurrent fresher push.
 *
 *   - mutation onSuccess  -> {@link mergeServerResponse}: write server data,
 *                            stamped at send time; a live push that already
 *                            advanced the marker is preserved.
 *   - live event          -> {@link applyLiveEvent}: write pushed data, stamped
 *                            now; biased to win ties.
 *
 * Freshness is tracked in a side-table keyed by the query key's stable hash,
 * NOT on the cached object itself — TanStack's structural sharing clones data
 * and would strip an attached marker. The side-table is the source of truth;
 * the symbol on the object is a best-effort convenience for callers holding a
 * raw value (e.g. local component state) outside the cache.
 */

/** Symbol marker holding the logical freshness time (epoch ms) of a value. */
export const FRESHNESS_MARKER: unique symbol = Symbol.for('tyndo.reconciliation.freshAt');

/** Source of a write, used only to break exact-timestamp ties. */
export type WriteSource = 'rest' | 'live';

interface Marked {
  [FRESHNESS_MARKER]?: number;
}

/** Read the logical freshness time stamped on a value (0 if unmarked). */
export function freshnessOf(value: unknown): number {
  if (value && typeof value === 'object') {
    const m = (value as Marked)[FRESHNESS_MARKER];
    if (typeof m === 'number') return m;
  }
  return 0;
}

/**
 * Stamp a value with a logical freshness time. Objects are stamped in-place via
 * a non-enumerable symbol property (invisible to JSON/render); primitives are
 * returned unchanged (their freshness is tracked by the caller's own clock).
 */
export function stampFreshness<T>(value: T, freshAt: number): T {
  if (value && typeof value === 'object') {
    Object.defineProperty(value as object, FRESHNESS_MARKER, {
      value: freshAt,
      enumerable: false,
      configurable: true,
      writable: true,
    });
  }
  return value;
}

/**
 * Decide the winner of a reconciliation between the value currently in the
 * cache and an incoming write. Pure + deterministic — the heart of the policy,
 * unit-testable without React or a QueryClient.
 *
 * @returns `incoming` if it should replace `current`, otherwise `current`.
 */
export function reconcile<T>(
  current: T | undefined,
  incoming: T,
  incomingAt: number,
  incomingSource: WriteSource,
): { value: T; changed: boolean } {
  if (current === undefined) {
    return { value: stampFreshness(incoming, incomingAt), changed: true };
  }
  const currentAt = freshnessOf(current);

  if (incomingAt > currentAt) {
    return { value: stampFreshness(incoming, incomingAt), changed: true };
  }
  if (incomingAt === currentAt && incomingSource === 'live') {
    // Tie-break: a live push wins over a same-instant REST write so a slow
    // response that raced the push can never roll the UI back.
    return { value: stampFreshness(incoming, incomingAt), changed: true };
  }
  // Incoming is older (or an equally-old REST write losing the tie): keep the
  // fresher cached value. The slow REST response is dropped.
  return { value: current, changed: false };
}

// ── Per-key freshness side-table ────────────────────────────────────────────
//
// Maps a stable hash of a query key to the logical freshness time of whatever
// value currently occupies that cache slot. Survives TanStack's structural
// sharing (which clones data and would drop an attached symbol marker).

const freshnessTable = new Map<string, number>();

/** Stable, order-sensitive hash of a query key (mirrors TanStack's hashing). */
export function hashQueryKey(queryKey: QueryKey): string {
  return JSON.stringify(queryKey, (_k, val) =>
    val && typeof val === 'object' && !Array.isArray(val)
      ? Object.keys(val)
          .sort()
          .reduce<Record<string, unknown>>((acc, k) => {
            acc[k] = (val as Record<string, unknown>)[k];
            return acc;
          }, {})
      : val,
  );
}

/** Read the freshness recorded for a cache slot (0 if never written here). */
export function freshnessForKey(queryKey: QueryKey): number {
  return freshnessTable.get(hashQueryKey(queryKey)) ?? 0;
}

/** Test/teardown helper: forget all recorded freshness. */
export function resetFreshnessTable(): void {
  freshnessTable.clear();
}

/**
 * Apply a live SignalR push to a cache entry using the reconciliation rule.
 * Stamps the push with `now` and biases it to win timestamp ties.
 *
 * Call this from a SignalR handler INSTEAD of blindly
 * `queryClient.setQueryData(key, data)` + `invalidateQueries`.
 *
 * @returns whether the push was applied (false ⇒ a fresher value was kept).
 */
export function applyLiveEvent<T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  data: T,
  now: number = Date.now(),
): boolean {
  const hash = hashQueryKey(queryKey);
  const currentAt = freshnessTable.get(hash) ?? 0;
  // Push wins ties (>=) so an out-of-order REST write at the same instant
  // cannot displace it.
  if (now < currentAt) return false;
  queryClient.setQueryData<T>(queryKey, stampFreshness(data, now));
  freshnessTable.set(hash, now);
  return true;
}

/**
 * Merge a mutation's server response into a cache entry on `onSuccess`.
 *
 * `sentAt` is the logical time the request was issued (NOT when it resolved):
 * the response describes state as of when the server processed it, which is
 * bounded above by send time + RTT. Using send time means a live push that
 * arrives DURING the round-trip (and is therefore newer than the state the
 * server saw) is not clobbered by the late-arriving response.
 *
 * Returns whether the response was applied (false ⇒ a fresher push won).
 */
export function mergeServerResponse<T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  response: T,
  sentAt: number,
): boolean {
  const hash = hashQueryKey(queryKey);
  const currentAt = freshnessTable.get(hash) ?? 0;
  // REST loses ties (strict >) so a same-instant live push wins.
  if (sentAt <= currentAt) return false;
  queryClient.setQueryData<T>(queryKey, stampFreshness(response, sentAt));
  freshnessTable.set(hash, sentAt);
  return true;
}

/**
 * Reconcile a mutation's `onSuccess` against the realtime cache instead of
 * blindly invalidating (A5-439).
 *
 * The race: a module's `useApiMutation` `onSuccess` invalidates a query that is
 * ALSO fed by a SignalR push. If the backend pushes a fresher live event
 * immediately after responding, a naive `invalidateQueries` discards the
 * just-pushed value and schedules a refetch that may briefly return the
 * pre-push state — a transient cache miss / flicker. The prior freshness guard
 * only covered first-hydration.
 *
 * This helper makes invalidation push-aware. Given the time the mutation was
 * SENT (`sentAt`, NOT when it resolved):
 *
 *   - If a live push fresher than `sentAt` already occupies the slot, the live
 *     event is the source of truth: we do NOT invalidate (skip the refetch
 *     that would roll the UI back), and report `{ invalidated: false }`.
 *   - Otherwise, if a server `response` is supplied, merge it via
 *     {@link mergeServerResponse} (write-through, no network round-trip).
 *   - Otherwise invalidate so TanStack refetches authoritative state.
 *
 * Modules wiring real-time queries should call this from `onSuccess` rather than
 * invalidating directly; for non-realtime queries a plain `invalidateApiQueries`
 * remains correct.
 *
 * @param sentAt logical send time of the mutation (capture `Date.now()` BEFORE
 *               calling `.mutate(...)`, or in the mutation's `onMutate`).
 * @returns whether an invalidation/merge was performed and which path was taken.
 */
export function reconcileOrInvalidate<T = unknown>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  sentAt: number,
  response?: T,
): { invalidated: boolean; merged: boolean; skippedForFresherPush: boolean } {
  const hash = hashQueryKey(queryKey);
  const currentAt = freshnessTable.get(hash) ?? 0;

  // A live push strictly fresher than the mutation's send time already holds
  // the slot — it supersedes anything this mutation knew. Leave it untouched.
  if (currentAt > sentAt) {
    return { invalidated: false, merged: false, skippedForFresherPush: true };
  }

  // No fresher push: if the server handed us the post-write state, write it
  // through (and advance the freshness marker) so no refetch flicker occurs.
  if (response !== undefined) {
    const merged = mergeServerResponse(queryClient, queryKey, response, sentAt);
    return { invalidated: false, merged, skippedForFresherPush: false };
  }

  // No response to merge: invalidate to fetch authoritative state.
  void queryClient.invalidateQueries({ queryKey });
  return { invalidated: true, merged: false, skippedForFresherPush: false };
}
